import { useFormContext } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Store, MapPin } from 'lucide-react';

/**
 * 超商取貨門市資訊表單
 * 用於超商取貨付款 (cvs_pickup_cod) 和超商取貨先付款 (cvs_pickup_prepaid)
 */
export function CvsStoreForm() {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext();

  const paymentMethod = watch('payment_method');
  const isCvsPickup = paymentMethod === 'cvs_pickup_cod' || paymentMethod === 'cvs_pickup_prepaid';

  // 如果不是超商取貨，不顯示此表單
  if (!isCvsPickup) {
    return null;
  }

  return (
    <div className="space-y-4">
      <Alert className="bg-blue-50 border-blue-200">
        <Store className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <p className="font-medium mb-2">📦 超商取貨說明</p>
          <ul className="text-sm space-y-1 list-disc list-inside">
            <li>請填寫您方便取貨的超商門市資訊</li>
            <li>訂單確認後，我們會將商品寄送至該門市</li>
            <li>到貨後會收到簡訊通知，請於 7 天內取貨</li>
            <li>未來將提供電子地圖選擇門市功能 🗺️</li>
          </ul>
        </AlertDescription>
      </Alert>

      <h3 className="text-lg font-semibold flex items-center gap-2">
        <Store className="h-5 w-5 text-blue-600" />
        取貨門市資訊
      </h3>

      {/* 門市名稱 */}
      <div className="space-y-2">
        <Label htmlFor="cvs_store_name" className="flex items-center gap-2">
          <Store className="h-4 w-4" />
          門市名稱 *
        </Label>
        <Input
          id="cvs_store_name"
          placeholder="例如：台北市政府門市、全家台北車站店"
          {...register('cvs_store_name', {
            required: isCvsPickup ? '請輸入超商門市名稱' : false,
          })}
          className={errors.cvs_store_name ? 'border-red-500' : ''}
        />
        {errors.cvs_store_name && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {errors.cvs_store_name.message}
          </p>
        )}
        <p className="text-xs text-gray-500">
          請輸入完整門市名稱，例如：7-11 台北市政府門市、全家 台北車站店
        </p>
      </div>

      {/* 門市地址 */}
      <div className="space-y-2">
        <Label htmlFor="cvs_store_address" className="flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          門市地址 *
        </Label>
        <Input
          id="cvs_store_address"
          placeholder="例如：台北市信義區市府路 1 號"
          {...register('cvs_store_address', {
            required: isCvsPickup ? '請輸入超商門市地址' : false,
          })}
          className={errors.cvs_store_address ? 'border-red-500' : ''}
        />
        {errors.cvs_store_address && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {errors.cvs_store_address.message}
          </p>
        )}
        <p className="text-xs text-gray-500">
          請輸入完整地址，方便我們確認寄送門市
        </p>
      </div>

      {/* 提示訊息 */}
      <Alert className="bg-yellow-50 border-yellow-200">
        <AlertCircle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-800 text-sm">
          💡 <strong>找不到門市資訊？</strong>
          <br />
          您可以到超商門市詢問店員，或使用超商官網的門市查詢功能：
          <ul className="mt-2 space-y-1">
            <li>• 7-11: <a href="https://www.ibon.com.tw/retail_inquiry.aspx" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">門市查詢</a></li>
            <li>• 全家: <a href="https://www.family.com.tw/marketing/inquiry.aspx" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">門市查詢</a></li>
            <li>• 萊爾富: <a href="https://www.hilife.com.tw/storeInquiry_street.aspx" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">門市查詢</a></li>
            <li>• OK 超商: <a href="https://www.okmart.com.tw/convenient_shopSearch" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">門市查詢</a></li>
          </ul>
        </AlertDescription>
      </Alert>
    </div>
  );
}
